/**
 * Branded modal alert — replaces native `window.alert()` so dialogs
 * carry the medpod logo (instead of the browser's default globe).
 *
 * Usage:
 *   await brandedAlert("Assign failed: HTTP 404");
 *   await brandedAlert("Save complete", { kind: "ok" });
 *
 * Mount the host once near the application root:
 *   <BrandedDialogHost />
 *
 * Why a custom modal instead of window.alert:
 *   - native alert() shows the page favicon (or, in some Chrome
 *     builds, a generic "globe" web-page icon) — neither matches the
 *     medpod brand,
 *   - native alert() blocks the renderer thread which interferes with
 *     solid-query refetch + react-query background work,
 *   - native alert() is unstyleable; product UX wants the dialog to
 *     match the rest of the admin UI (ink palette, brand chip).
 *
 * The dialog is intentionally minimal: a single <Esc> / Close action.
 * Confirm- and prompt-shaped flows should use `brandedConfirm` /
 * `brandedPrompt` (TODO — currently still native) when they need
 * Yes/No or text input.
 */
import { createSignal, For, onCleanup, onMount, Show } from "solid-js";

type AlertKind = "info" | "ok" | "warn" | "error";

interface AlertEntry {
  id:      number;
  message: string;
  kind:    AlertKind;
  resolve: () => void;
}

const [queue, setQueue] = createSignal<AlertEntry[]>([]);
let nextId = 1;

export function brandedAlert(message: string, opts: { kind?: AlertKind } = {}): Promise<void> {
  return new Promise((resolve) => {
    setQueue([...queue(), {
      id:      nextId++,
      message,
      kind:    opts.kind ?? "error",
      resolve,
    }]);
  });
}

function dismiss(id: number) {
  const remaining: AlertEntry[] = [];
  for (const e of queue()) {
    if (e.id === id) e.resolve();
    else remaining.push(e);
  }
  setQueue(remaining);
}

const KIND_ACCENT: Record<AlertKind, string> = {
  info:  "text-ink-700",
  ok:    "text-ok",
  warn:  "text-warn",
  error: "text-danger",
};

export function BrandedDialogHost() {
  // Esc closes the front-most alert.
  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && queue().length > 0) {
        const front = queue()[queue().length - 1];
        dismiss(front.id);
      }
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  return (
    <Show when={queue().length > 0}>
      <div class="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
           role="presentation"
           onClick={() => {
             const front = queue()[queue().length - 1];
             if (front) dismiss(front.id);
           }}>
        <For each={queue()}>{(entry, i) => (
          <div class="absolute"
               style={{ transform: `translate(${i() * 12}px, ${i() * 12}px)` }}
               onClick={(e) => e.stopPropagation()}>
            <div role="alertdialog"
                 aria-modal="true"
                 aria-label={entry.message}
                 class="w-[min(92vw,440px)] rounded-md border border-ink-300 bg-ink-100 shadow-2xl">
              <header class="flex items-center gap-3 border-b border-ink-300 px-4 py-3">
                {/* Brand mark — the medpod 4-petal clover lives at /logo.png */}
                <img src="/logo.png" alt="" width="28" height="28"
                     class="shrink-0 select-none" draggable={false} />
                <div class="flex-1">
                  <div class={"text-xs font-semibold uppercase tracking-wider " + KIND_ACCENT[entry.kind]}>
                    {entry.kind === "ok" ? "Done"
                      : entry.kind === "warn"  ? "Warning"
                      : entry.kind === "error" ? "Error"
                      :                          "Notice"}
                  </div>
                  <div class="text-[11px] text-ink-700">medpod</div>
                </div>
                <button type="button"
                        class="btn-ghost text-xs"
                        aria-label="Close"
                        onClick={() => dismiss(entry.id)}>×</button>
              </header>
              <div class="px-4 py-3 text-sm text-ink-1000 whitespace-pre-line">{entry.message}</div>
              <footer class="flex justify-end gap-2 border-t border-ink-300 px-4 py-2">
                <button type="button"
                        class="btn-primary text-xs"
                        onClick={() => dismiss(entry.id)}>OK</button>
              </footer>
            </div>
          </div>
        )}</For>
      </div>
    </Show>
  );
}
